import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { OneMealService } from "../../services/OneMealService";
import ChefForPartyService from "../../services/ChefForPartyService";
import AppConstantService from "../../services/AppConstantService";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";

const oneMealService = new OneMealService();
const chefForPartyService = new ChefForPartyService();
const appConstantService = new AppConstantService();

export type CookIncentiveServiceType = "oneMeal" | "party";
export type CookIncentiveType = "lunchMoney" | "farVisitIncentive";

// Per-incentive copy, endpoints and cap constant. Both incentives behave identically:
// the admin types an amount, the backend caps it at the max-amount app constant and
// auto-credits the cook's wallet only below the configured threshold.
const INCENTIVE_CONFIG: Record<
  CookIncentiveType,
  {
    title: string;
    label: string;
    amountFieldLabel: string;
    maxAmountConstant: string;
    // Fallback for the input hint only; the backend is the one that enforces the cap.
    defaultMaxAmount: number;
    submit: (
      serviceType: CookIncentiveServiceType,
      id: number,
      payload: { amount: number; comments?: string }
    ) => Promise<any>;
  }
> = {
  lunchMoney: {
    title: "Process Lunch Money",
    label: "Lunch money",
    amountFieldLabel: "Lunch Money Amount (₹)",
    maxAmountConstant: "LUNCH_MONEY_MAX_AMOUNT",
    defaultMaxAmount: 200,
    submit: (serviceType, id, payload) =>
      serviceType === "party"
        ? chefForPartyService.processLunchMoney(id, payload)
        : oneMealService.processLunchMoney(id, payload),
  },
  farVisitIncentive: {
    title: "Process Far Visit Incentive",
    label: "Far visit incentive",
    amountFieldLabel: "Far Visit Incentive Amount (₹)",
    maxAmountConstant: "FAR_VISIT_INCENTIVE_MAX_AMOUNT",
    defaultMaxAmount: 200,
    submit: (serviceType, id, payload) =>
      serviceType === "party"
        ? chefForPartyService.processFarVisitIncentive(id, payload)
        : oneMealService.processFarVisitIncentive(id, payload),
  },
};

interface Props {
  open: boolean;
  id: number;
  serviceType: CookIncentiveServiceType;
  incentiveType: CookIncentiveType;
  callback: () => void;
}

const CookIncentiveDialog = (props: Props) => {
  const config = INCENTIVE_CONFIG[props.incentiveType];
  const [amount, setAmount] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [maxAmount, setMaxAmount] = useState<number>(config.defaultMaxAmount);

  useEffect(() => {
    if (props.open) {
      setAmount("");
      setComments("");
      setSubmitting(false);
      appConstantService
        .fetchAppConstantValueByName(config.maxAmountConstant)
        .then((value) => {
          const configuredMax = Number(value);
          setMaxAmount(
            !value || isNaN(configuredMax) || configuredMax <= 0 ? config.defaultMaxAmount : configuredMax
          );
        })
        .catch(() => setMaxAmount(config.defaultMaxAmount));
    }
  }, [props.open, config.maxAmountConstant, config.defaultMaxAmount]);

  const handleClose = () => {
    props.callback();
  };

  const handleSubmit = () => {
    const numericAmount = Number(amount);
    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: `Please enter a valid ${config.label.toLowerCase()} amount greater than zero.`,
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    if (numericAmount > maxAmount) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: `${config.label} cannot exceed ₹${maxAmount} per service.`,
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    setSubmitting(true);
    config
      .submit(props.serviceType, props.id, { amount: numericAmount, comments: comments || undefined })
      .then((res: any) => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: res?.message || `${config.label} processed successfully.`,
          type: CustomSnackbarTypes.SUCCESS,
        });
        handleClose();
      })
      .catch(() => {
        setSubmitting(false);
      });
  };

  return (
    <Dialog onClose={handleClose} open={props.open} scroll={"paper"} maxWidth="xl">
      <DialogTitle>
        <Typography fontWeight={"600"}>{config.title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: "320px", display: "flex", flexDirection: "column", gap: "16px", mt: "4px" }}>
          <TextField
            label={config.amountFieldLabel}
            value={amount}
            type={"number"}
            fullWidth
            InputProps={{ inputProps: { min: 1, max: maxAmount } }}
            error={!!amount && Number(amount) > maxAmount}
            helperText={`Maximum ₹${maxAmount} per service.`}
            onChange={(event) => setAmount(event.target.value)}
          />
          <TextField
            label="Comments (optional)"
            value={comments}
            fullWidth
            multiline
            minRows={2}
            onChange={(event) => setComments(event.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions style={{ marginTop: "12px" }}>
        <Button variant="contained" color={"error"} onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookIncentiveDialog;
